import { useCallback, useEffect, useRef, useState } from 'react';
import { useModal } from 'hooks/useModal';
import { ImageCropperModal, type ImageCropperOptions } from 'components/ImageCropper';

interface CropRequest {
  id: number;
  file: File;
  options: ImageCropperOptions;
}

/** Child effects run before the parent's, so this lands before the hook checks it. */
function MountProbe({ onMount }: { onMount: () => void }) {
  useEffect(onMount, [onMount]);
  return null;
}

/**
 * Promise-style cropper: `cropImage(file)` opens the modal and resolves with the
 * cropped File, or null if the player backs out. Render `CropperModal` once in the
 * calling component — a crop resolves null (and complains) if you forget, rather
 * than hanging on a promise nothing can settle.
 */
export function useImageCropper() {
  const { isOpen, openModal, closeModal } = useModal();
  const [request, setRequest] = useState<CropRequest | null>(null);
  const resolveRef = useRef<((file: File | null) => void) | null>(null);
  const requestIdRef = useRef(0);
  const isMountedRef = useRef(false);

  const settle = useCallback(
    (result: File | null) => {
      resolveRef.current?.(result);
      resolveRef.current = null;
      closeModal();
    },
    [closeModal],
  );

  const markMounted = useCallback(() => {
    isMountedRef.current = true;
  }, []);

  const cropImage = useCallback(
    (file: File, options: ImageCropperOptions = {}) =>
      new Promise<File | null>((resolve) => {
        // A second pick while one is open supersedes it.
        resolveRef.current?.(null);
        resolveRef.current = resolve;
        requestIdRef.current += 1;
        isMountedRef.current = false;
        setRequest({ id: requestIdRef.current, file, options });
        openModal();
      }),
    [openModal],
  );

  // Nothing can settle the promise if the node was never rendered.
  useEffect(() => {
    if (!request || isMountedRef.current) return;
    // eslint-disable-next-line no-console
    console.error('useImageCropper: render `CropperModal` for cropImage to resolve.');
    settle(null);
  }, [request, settle]);

  // Keyed per request so zoom/crop state starts fresh for every new photo.
  const CropperModal = request ? (
    <>
      <MountProbe key={request.id} onMount={markMounted} />
      <ImageCropperModal
        key={request.id}
        appear
        isOpen={isOpen}
        file={request.file}
        {...request.options}
        onCancel={() => settle(null)}
        onApply={settle}
      />
    </>
  ) : null;

  return { cropImage, CropperModal };
}
